import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { useData } from '../../store/DataContext';
import { MenuIcon, XIcon, ChevronLeftIcon, ChevronRightIcon, LayoutDashboardIcon, PillIcon, PackageCheckIcon, ClipboardListIcon, TruckIcon, SlidersHorizontalIcon, ArrowLeftRightIcon, CalendarX2Icon, AlertTriangleIcon, BarChart3Icon, HistoryIcon, FileTextIcon } from 'lucide-react';
import { PharmacyOverview } from './PharmacyOverview';
import { InventoryList } from './InventoryList';
import { ReceiveStock } from './ReceiveStock';
import { PurchaseOrders } from './PurchaseOrders';
import { Suppliers } from './Suppliers';
import { StockActions } from './StockActions';
import { ExpiredMedicines } from './ExpiredMedicines';
import { LowStockAlerts } from './LowStockAlerts';
import { InventoryReports } from './InventoryReports';
import { StockMovementHistory } from './StockMovementHistory';
import { PharmacyDispensing } from './PharmacyDispensing';
import { expiryAlertWindow, isLowStockOnly, isOutOfStock } from './pharmacyUtils';
export type PharmacySection = 'overview' | 'dispensing' | 'inventory' | 'receive-stock' | 'purchase-orders' | 'suppliers' | 'adjustments' | 'returns' | 'expired' | 'low-stock' | 'reports' | 'movement-history';
type NavItem = {
  id: PharmacySection;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
  count?: number;
  tone?: 'red' | 'amber' | 'blue';
};
type NavGroup = {
  label: string;
  items: NavItem[];
};
export function PharmacyModule() {
  const {
    currentUser,
    medicines,
    medicineBatches,
    purchaseOrders
  } = useData();
  const [section, setSection] = useState<PharmacySection>('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const alerts = useMemo(() => ({
    low: medicines.filter(isLowStockOnly).length,
    out: medicines.filter(isOutOfStock).length,
    expiring: medicineBatches.filter(batch => expiryAlertWindow(batch) !== null).length,
    purchaseOrders: purchaseOrders.filter(order => order.status === 'Pending').length
  }), [medicineBatches, medicines, purchaseOrders]);
  if (!currentUser || !['Pharmacy', 'Pharmacy Manager'].includes(currentUser.role)) return <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center text-red-700" data-id="element-4820">
        You do not have permission to access the Pharmacy workspace.
      </div>;
  const groups: NavGroup[] = [{
    label: 'Workspace',
    items: [{
      id: 'overview',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }, {
      id: 'dispensing',
      label: 'Dispensing',
      icon: ClipboardListIcon
    }]
  }, {
    label: 'Inventory',
    items: [{
      id: 'inventory',
      label: 'Pharmacy Inventory',
      icon: PillIcon
    }, {
      id: 'receive-stock',
      label: 'Receive New Stock',
      icon: PackageCheckIcon
    }, {
      id: 'adjustments',
      label: 'Stock Adjustments',
      icon: SlidersHorizontalIcon
    }, {
      id: 'returns',
      label: 'Medicine Returns',
      icon: ArrowLeftRightIcon
    }, {
      id: 'expired',
      label: 'Expired Medicines',
      icon: CalendarX2Icon,
      count: alerts.expiring,
      tone: medicineBatches.some(batch => expiryAlertWindow(batch) === 'Expired') ? 'red' : 'amber'
    }, {
      id: 'low-stock',
      label: 'Low Stock Alerts',
      icon: AlertTriangleIcon,
      count: alerts.low + alerts.out + alerts.expiring,
      tone: alerts.out ? 'red' : 'amber'
    }]
  }, {
    label: 'Procurement',
    items: [{
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: FileTextIcon,
      count: alerts.purchaseOrders,
      tone: 'blue'
    }, {
      id: 'suppliers',
      label: 'Suppliers',
      icon: TruckIcon
    }]
  }, {
    label: 'Insights',
    items: [{
      id: 'reports',
      label: 'Inventory Reports',
      icon: BarChart3Icon
    }, {
      id: 'movement-history',
      label: 'Stock Movement History',
      icon: HistoryIcon
    }]
  }];
  const select = (next: PharmacySection) => {
    setSection(next);
    setMobileOpen(false);
  };
  const content = () => {
    switch (section) {
      case 'overview':
        return <PharmacyOverview onNavigate={select} data-id="element-4821" />;
      case 'dispensing':
        return <PharmacyDispensing onBack={() => select('overview')} data-id="element-4822" />;
      case 'inventory':
        return <InventoryList data-id="element-4823" />;
      case 'receive-stock':
        return <ReceiveStock data-id="element-4824" />;
      case 'purchase-orders':
        return <PurchaseOrders data-id="element-4825" />;
      case 'suppliers':
        return <Suppliers data-id="element-4826" />;
      case 'adjustments':
        return <StockActions mode="adjustment" data-id="element-4827" />;
      case 'returns':
        return <StockActions mode="return" data-id="element-4828" />;
      case 'expired':
        return <ExpiredMedicines data-id="element-4829" />;
      case 'low-stock':
        return <LowStockAlerts data-id="element-4830" />;
      case 'reports':
        return <InventoryReports data-id="element-4831" />;
      case 'movement-history':
        return <StockMovementHistory data-id="element-4832" />;
    }
  };
  const navigation = (mobile = false) => <nav className="space-y-5" aria-label="Pharmacy navigation" data-id="element-4833">
      {groups.map(group => <div key={group.label} data-id="element-4834">
          <p className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 ${collapsed && !mobile ? 'sr-only' : ''}`} data-id="element-4835">
            {group.label}
          </p>
          <div className="space-y-0.5" data-id="element-4836">
            {group.items.map(item => {
          const Icon = item.icon;
          const active = section === item.id;
          return <button key={item.id} type="button" onClick={() => select(item.id)} title={collapsed && !mobile ? item.label : undefined} className={`group flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${active ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950'} ${collapsed && !mobile ? 'justify-center px-2' : ''}`} data-id="element-4837">
                  <Icon className={`h-4 w-4 shrink-0 ${active ? 'text-teal-700' : 'text-slate-400 group-hover:text-slate-600'} ${collapsed && !mobile ? '' : 'mr-3'}`} data-id="element-4838" />
                  {(!collapsed || mobile) && <>
                      <span className="truncate" data-id="element-4839">{item.label}</span>
                      {item.count ? <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-[11px] font-bold ${item.tone === 'red' ? 'bg-red-100 text-red-700' : item.tone === 'amber' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-4840">
                          {item.count}
                        </span> : null}
                    </>}
                </button>;
        })}
          </div>
        </div>)}
    </nav>;
  return <div className="-m-4 flex min-h-[calc(100vh-4rem)] bg-slate-50 sm:-m-6 lg:-m-8" data-id="element-4841">
      <aside className={`hidden shrink-0 border-r border-slate-200 bg-white lg:flex lg:flex-col ${collapsed ? 'w-[76px]' : 'w-[264px]'} transition-[width] duration-200`} data-id="element-4842">
        <div className={`border-b border-slate-100 ${collapsed ? 'p-3' : 'p-5'}`} data-id="element-4843">
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`} data-id="element-4844">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-700" data-id="element-4845">
              <PillIcon className="h-5 w-5" data-id="element-4846" />
            </div>
            {!collapsed && <div data-id="element-4847">
                <p className="text-sm font-bold text-slate-900" data-id="element-4848">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-4849">
                  Pharmacy Operations
                </p>
              </div>}
          </div>
          {!collapsed && <span className="mt-4 inline-flex rounded-md border border-teal-100 bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-800" data-id="element-4850">
              Live inventory ledger
            </span>}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-5" data-id="element-4851">{navigation()}</div>
        <div className="border-t border-slate-100 p-3" data-id="element-4852">
          <button type="button" onClick={() => setCollapsed(value => !value)} className="flex w-full items-center justify-center rounded-md py-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700" aria-label={collapsed ? 'Expand pharmacy navigation' : 'Collapse pharmacy navigation'} data-id="element-4853">
            {collapsed ? <ChevronRightIcon className="h-4 w-4" data-id="element-4854" /> : <ChevronLeftIcon className="h-4 w-4" data-id="element-4855" />}
          </button>
        </div>
      </aside>
      {mobileOpen && <div className="fixed inset-0 z-50 flex lg:hidden" data-id="element-4856">
          <button type="button" aria-label="Close pharmacy navigation" className="absolute inset-0 bg-slate-950/35" onClick={() => setMobileOpen(false)} data-id="element-4857" />
          <aside className="relative flex h-full w-[280px] flex-col bg-white shadow-2xl" data-id="element-4858">
            <div className="flex items-center justify-between border-b border-slate-100 p-5" data-id="element-4859">
              <div data-id="element-4860">
                <p className="text-sm font-bold text-slate-900" data-id="element-4861">BMED Care</p>
                <p className="text-xs font-medium text-pink-600" data-id="element-4862">
                  Pharmacy Operations
                </p>
              </div>
              <button type="button" onClick={() => setMobileOpen(false)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Close pharmacy navigation" data-id="element-4863">
                <XIcon className="h-5 w-5" data-id="element-4864" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-4865">{navigation(true)}</div>
          </aside>
        </div>}
      <section className="min-w-0 flex-1" data-id="element-4866">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6 lg:px-8" data-id="element-4867">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4" data-id="element-4868">
            <div className="flex min-w-0 items-center gap-3" data-id="element-4869">
              <button type="button" onClick={() => setMobileOpen(true)} className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 lg:hidden" aria-label="Open pharmacy navigation" data-id="element-4870">
                <MenuIcon className="h-5 w-5" data-id="element-4871" />
              </button>
              <div data-id="element-4872">
                <p className="text-xs font-medium text-slate-500" data-id="element-4873">
                  Pharmacy <span className="mx-1 text-slate-300" data-id="element-4874">/</span>{' '}
                  Operations
                </p>
                <h1 className="mt-0.5 text-lg font-bold text-slate-900 sm:text-xl" data-id="element-4875">
                  {groups.flatMap(group => group.items).find(item => item.id === section)?.label}
                </h1>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700" data-id="element-4876">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-4877" /> Live
              data
            </span>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8" data-id="element-4878">
          {content()}
        </main>
      </section>
    </div>;
}